/**
 * Canal de tiempo real.
 *
 * Supabase Realtime avisa que una fila cambio; NO trae los datos. Al recibir el
 * aviso se invalidan las consultas afectadas y TanStack Query vuelve a pedirlas,
 * pasando otra vez por RLS. Escribir la cache directamente desde el canal
 * obligaria a duplicar el control de acceso en el cliente, y el cliente es
 * justo donde no se puede confiar.
 *
 * Resultado practico: la dueña abre el panel en el celular y ve la venta que el
 * cajero acaba de cobrar, sin recargar.
 *
 * El canal respeta RLS: solo llegan cambios de filas que esa sesion podria leer,
 * asi que una sucursal jamas recibe el movimiento de otra.
 */

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { supabase } from './supabase.js';
import { recordApiFailure } from './error-log.js';
import REALTIME_TABLES from './realtime-tables.json';

export type ConnectionState = 'connecting' | 'online' | 'offline';

/**
 * Estado de la conexion compartido con toda la aplicacion.
 *
 * `useRealtime` ABRE un canal, asi que solo puede llamarse una vez —lo hace el
 * marco— y el resto de las pantallas leen el estado de aqui.
 */
export const RealtimeContext = createContext<ConnectionState>('connecting');

export function useConnection(): ConnectionState {
  return useContext(RealtimeContext);
}

/**
 * Las tablas del canal y su alcance, LEIDAS DE `realtime-tables.json`.
 *
 * Viven en un JSON y no aqui porque `npm run audit:security` se suscribe con
 * sesion exactamente a este canal —las mismas tablas, los mismos filtros— y
 * exige que el servidor lo acepte. Un script de Node no puede importar este
 * modulo (React, supabase-js del navegador), y una segunda lista escrita a mano
 * en el script es la clase de copia que un dia difiere sin que nadie lo note.
 *
 *   `store`  la tabla lleva `store_id` y el servidor filtra por sucursal.
 *   `org`    no lleva `store_id`; la RLS de organizacion decide que filas llegan.
 *
 * Se valida con Zod al cargar: un valor que no sea `store` u `org` revienta al
 * arrancar en desarrollo, que es el momento barato de enterarse.
 */
export const REALTIME_TABLE_SCOPES = z
  .record(z.string(), z.enum(['store', 'org']))
  .parse(REALTIME_TABLES);

/**
 * Que consultas invalida cada tabla.
 *
 * Antes el servidor mandaba eventos con nombre ('sale.created'); ahora la señal
 * es la tabla que cambio, que es mas dificil de olvidar: si alguien agrega una
 * operacion nueva sobre `sales`, el aviso sale solo.
 *
 * Toda tabla de aqui tiene que estar en `realtime-tables.json`, y al reves; lo
 * afirma `realtime-tables.test.ts`.
 */
export const INVALIDATION: Record<string, string[]> = {
  sales: ['dashboard', 'sales', 'catalog', 'cash', 'credit'],
  store_products: ['catalog', 'dashboard', 'intelligence', 'movements'],
  inventory_movements: ['catalog', 'dashboard', 'intelligence', 'movements'],
  customer_credit_movements: ['credit', 'cash', 'dashboard'],
  customers: ['credit'],
  cash_sessions: ['cash', 'dashboard'],
  cash_movements: ['cash'],
  // Solo viaja la CABECERA del lote: los renglones llevan costo, y Realtime
  // respeta la RLS de fila pero no los privilegios de columna. Con que cambie la
  // cabecera basta — la bandeja se recarga por RPC, que es donde si se decide
  // quien ve que.
  stock_intakes: ['intakes', 'catalog', 'intelligence'],
  // La campanita se enciende sola: el aviso de las 18:15 llega a la tablet del
  // mostrador sin que nadie cambie de pantalla. Solo viaja la CABECERA del
  // aviso —el texto del reporte lleva el importe del dia—; con que sepamos que
  // entro uno basta, porque la bandeja se recarga por RPC, que es donde se
  // decide quien ve que. Ver la migracion 20260813212500.
  notifications: ['notifications'],
};

/** Tablas que llevan `store_id` y por tanto se filtran en el servidor. */
const SCOPED = new Set(
  Object.entries(REALTIME_TABLE_SCOPES)
    .filter(([, scope]) => scope === 'store')
    .map(([table]) => table),
);

/**
 * El aviso `system` con que el servidor cuenta que la suscripcion FALLO.
 *
 * Realtime contesta el `phx_join` con `ok` —y realtime-js marca SUBSCRIBED— antes
 * de intentar la suscripcion en Postgres. Si esa falla, lo dice un segundo
 * despues por este evento, que realtime-js NO le pasa al callback de
 * `subscribe`. Sin escucharlo, el indicador dice "En vivo" sobre un canal que
 * no va a entregar nada: asi estuvo del 13 de agosto al 3 de septiembre de 2026,
 * porque `stock_intakes` no tenia permiso de lectura para `authenticated` y el
 * filtro por sucursal no se podia validar (CLAUDE.md §13).
 */
function isSubscribeError(payload: unknown): payload is { status: 'error'; message?: string } {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'status' in payload &&
    (payload as { status: unknown }).status === 'error'
  );
}

export function useRealtime(storeId: number | null): ConnectionState {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ConnectionState>('connecting');
  // Los avisos llegan en rafaga (una venta toca cuatro tablas). Se agrupan para
  // no disparar veinte refetch por un solo cobro.
  const pending = useRef<Set<string>>(new Set());
  const flushTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (storeId === null) return;

    const flush = (): void => {
      const keys = [...pending.current];
      pending.current.clear();
      /*
       * `cancelRefetch: false`: si la consulta YA se esta pidiendo, se espera esa
       * respuesta en vez de cancelarla y volver a pedir. Es el caso de cada
       * venta propia: el `onSuccess` de la Caja ya invalido la cuadricula y el
       * aviso del canal llega 200 ms despues por la MISMA venta. Cancelar y
       * repetir era pagar dos veces la misma respuesta. Un aviso que llegue con
       * la lectura ya terminada si vuelve a pedir, que es lo correcto: puede ser
       * la venta de otra caja.
       */
      for (const key of keys) {
        void queryClient.invalidateQueries({ queryKey: [key] }, { cancelRefetch: false });
      }
    };

    const schedule = (table: string): void => {
      for (const key of INVALIDATION[table] ?? []) pending.current.add(key);
      if (flushTimer.current !== undefined) window.clearTimeout(flushTimer.current);
      flushTimer.current = window.setTimeout(flush, 120);
    };

    const channel = supabase.channel(`sucursal:${storeId}`);

    for (const table of Object.keys(INVALIDATION)) {
      channel.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table,
          ...(SCOPED.has(table) ? { filter: `store_id=eq.${storeId}` } : {}),
        },
        () => schedule(table),
      );
    }

    channel.on('system', {}, (payload: unknown) => {
      if (!isSubscribeError(payload)) return;
      // El canal quedo unido pero sordo. Decirlo es lo unico honesto: las
      // pantallas siguen funcionando por sus propias consultas, y quien lea
      // "Sin conexion" sabe que lo que ve puede estar desactualizado.
      setState('offline');
      recordApiFailure(`realtime:sucursal:${storeId}`, 'SUBSCRIBE', {
        code: 'REALTIME_SUBSCRIBE_FAILED',
        status: 0,
      });
      console.error('[realtime] el servidor rechazo la suscripcion:', payload.message ?? '(sin detalle)');
    });

    /*
     * EL JOIN TIENE QUE VIAJAR CON EL JWT DE LA SESION, y eso hay que esperarlo.
     *
     * `subscribe()` arma el join con el token que el socket tenga EN ESE
     * INSTANTE; si la sesion todavia no llego al socket, el join sale sin token
     * y el servidor evalua la suscripcion como `anon`, que no puede leer ninguna
     * columna: la suscripcion entera se rechaza —y solo se entera quien escuche
     * `system`, ver arriba—. Medido el 3 de septiembre de 2026 con la sesion real
     * del empleado: seis sockets nuevos, seis rechazos; con `setAuth()` esperado,
     * cero. Que el token suela llegar antes por su cuenta no es una garantia, y
     * en una caja registradora "suele" es "un dia no".
     */
    let cancelled = false;
    void (async () => {
      try {
        await supabase.realtime.setAuth();
      } catch {
        // Sin sesion el join sale con la llave publica y el servidor lo
        // rechaza: se vera como "Sin conexion", que es la verdad.
      }
      if (cancelled) return;
      channel.subscribe((status) => {
        if (status === 'SUBSCRIBED') setState('online');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') setState('offline');
        else if (status === 'CLOSED') setState('offline');
        else setState('connecting');
      });
    })();

    // Al volver a la pantalla tras dejar el celular bloqueado, el canal suele
    // estar muerto aunque el navegador no lo haya notificado.
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      void queryClient.invalidateQueries();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (flushTimer.current !== undefined) window.clearTimeout(flushTimer.current);
      void supabase.removeChannel(channel);
    };
  }, [storeId, queryClient]);

  return state;
}
